/**
 * Générateur — Automatisme : identifier la structure d'un motif évolutif en
 * repérant une régularité.
 * Attendu ID : 1062 | Niveau : 5ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 602 ; l'objectif
 * 6e mot pour mot). Cadrage du sous-agent programme-maths, arbitré par le prof
 * à l'épreuve de Musk les 2026-09-09 et 2026-09-10.
 *
 * Trois attendus, trois gestes, et c'est la NATURE DE LA RÉPONSE qui trie :
 *   1060  l'étape SUIVANTE (poursuivre) ;
 *   1061  un NOMBRE, à un rang donné ;
 *   1062  un CALCUL non réduit, qui décrit la fabrication : « 1 + 10 × 4 ».
 * « On ajoute 4 à chaque fois » est 1060, « à l'étape 3 il y en a 13 » est
 * 1061 ; ici le « + 4 » récursif doit devenir le « × 4 » fonctionnel (Eduscol
 * patterns p. 9, élève 2 : « trois points de départ auxquels on ajoutait
 * quatre groupes de n points »). La LETTRE reste dehors : « produire des
 * formules » est un objectif d'apprentissage (l. 606) et gen_932 a déjà son
 * QCM de formules ; le cycle 3 (l. 1072) réserve la lettre au cycle 4 formel.
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash          (une figure, deux lignes, le groupe imprimé :
 *                           « Étape 3 : ⬚ + ⬚ × 4 » puis « Étape 20 : ⬚ + ⬚ ×
 *                           4 = ⬚ », le résultat seulement si N ≤ 20)
 *   - Ce qui ne change pas (QCM « Quel calcul donne le nombre de points à
 *                           l'étape 10 ? » 3 choix ; Vrai/Faux 3 phrases sur
 *                           trois étapes, mêlant le calcul et les groupes)
 * Supprimés à l'épreuve de Musk : la lettre et toute formule en n (932), le
 * coloriage et la description en phrase libre (pas de format, et Eduscol
 * patterns p. 12 : « l'objectif n'est pas de savoir rédiger rigoureusement la
 * description »), la valeur du calcul (1061), l'étape suivante (1060), le
 * drag, la saisie nue, le sens inverse (équation, 4e).
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Figures STRUCTURÉES seulement (la part fixe et les groupes se voient, la
 * longueur d'une branche est le numéro de l'étape), banque commune aux trois
 * attendus : cf. ./motifs.js. Les rangées en sont exclues ici (un alignement
 * ne montre aucune structure), les figures à ajouts croissants aussi (leur
 * structure est n², objectifs Puissances).
 * Étape demandée de 5 à 20, et une fois sur deux 25, 50 ou 100 : le rang
 * lointain est GRATUIT ici puisque la valeur n'est jamais calculée, et c'est
 * lui qui montre que l'itération est morte (arbitrage 2026-09-10 ; en 1061 au
 * contraire il resterait 99 × 4 à faire, hors calcul mental).
 * Le calcul est toujours « fixe + N × groupe », dans cet ordre.
 *
 * SACRIFIÉ pour le coup de pouce : le couple (fixe 1, groupe 3), la frise de
 * carrés en allumettes, l'image de 638 et de 1060 ; exclu de toutes les cartes.
 *
 * ── Distracteurs (patterns p. 9 les deux relations, revues 1060 et 1061) ──
 *   ajout_de_trop         : (fixe + groupe) + N × groupe, le premier motif
 *                           entier pris pour la part fixe — L'ERREUR
 *                           PRINCIPALE, la collision avec le geste de 1060 ;
 *   ajout_manquant        : fixe + (N − 1) × groupe ;
 *   proportionnel         : N × groupe, oubli de la part fixe ;
 *   fixe_ajout_confondus  : groupe + N × fixe (si fixe ≠ groupe) ;
 *   addition              : fixe + N + groupe ;
 *   pas_mal_lu            : N groupes de (groupe ± 1) — au Vrai/Faux ;
 *                           sur les allumettes, c'est le côté partagé compté
 *                           deux fois (4 par carré au lieu de 3).
 * Chaque distracteur est différent de la clé EN VALEUR (les deux lectures
 * « 1 + 10 × 4 » et « 5 + 9 × 4 » valent 41 : la seconde n'est jamais un
 * distracteur, et elle n'est pas non plus une ligne vraie, retirée par le prof
 * le 2026-09-10 : une vérité qui contredit l'écriture qu'on installe n'est pas
 * un distracteur). Au QCM, un des deux distracteurs a toujours la forme de la
 * clé (a + N × b), sinon la forme seule donnerait la réponse.
 */
import { randomChoice, randInt, shuffle, makeQcm, makeVraiFaux, makeTexteTrou } from '../../../utils';
import { tirerLineaire, elImage, paramsMotif, sansRepetition, deNom, pluriel } from './motifs';

const ATTENDU_ID = 1062;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_FIXE = 'Ce qui ne change pas';

const SACRIFIE = { fixe: 1, groupe: 3 };
const LOINTAINES = [25, 50, 100];
/** Une fois sur deux un rang 5 à 20, une fois sur deux 25, 50 ou 100 (jamais calculé ici). */
const etape = () => (randomChoice([true, false]) ? randInt(5, 20) : randomChoice(LOINTAINES));
const tirer = () => tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true });

/** « 1 + 10 × 4 », toujours dans cet ordre : part fixe, numéro de l'étape, groupe. */
const calcul = (fixe, N, groupe) => `${fixe} + ${N} × ${groupe}`;

const EXPLICATIONS = (m) => ({
    regle: `${pluriel(m.fixe, m.nom)} au départ, puis ${m.groupe} par étape : à l'étape N, il y a ${m.fixe} + N × ${m.groupe}.`,
    ajout_de_trop: `${m.fixe + m.groupe}, c'est déjà l'étape 1 entière : au départ, il y a ${pluriel(m.fixe, m.nom)}.`,
    ajout_manquant: `À l'étape N, il y a N groupes, pas N − 1.`,
    proportionnel: `${pluriel(m.fixe, m.nom)} au départ : il faut ${m.fixe > 1 ? 'les' : 'le'} compter.`,
    fixe_ajout_confondus: `Au départ il y a ${m.fixe} ; ce qui s'ajoute, c'est ${m.groupe} par étape.`,
    addition: `N groupes de ${m.groupe}, c'est N × ${m.groupe}, pas N + ${m.groupe}.`,
    pas_mal_lu: `Compte ce qui s'ajoute d'une étape à l'autre : ${pluriel(m.groupe, m.nom)} par groupe.`,
});

// ─── Carte 1 : Série flash (une figure, deux lignes, quatre trous) ────────

const genFlash = () => {
    const m = tirer();
    const N = etape();
    // La seconde ligne va jusqu'au RÉSULTAT quand le rang reste mental (≤ 20) :
    // les deux premiers trous sont la structure (1062), le troisième ce qu'on en
    // fait. Si l'élève rate les deux premiers, c'est la structure qui manque ;
    // s'il les a et rate le dernier, c'est le calcul. Le diagnostic reste
    // lisible, ce qui était l'objection à mêler les deux (prof 2026-09-10).
    // Aux rangs 25, 50 et 100, la ligne s'arrête au calcul : 1 + 100 × 4 n'est
    // plus du calcul mental.
    const resultat = N <= 20 ? ` = {{${m.valeur(N)}}}` : '';
    const phrase = `Étape 3 : {{${m.fixe}}} + {{3}} × ${m.groupe}\nÉtape ${N} : {{${m.fixe}}} + {{${N}}} × ${m.groupe}${resultat}`;
    const texte = `<p><b>Complète le calcul du nombre ${deNom(m.nom)}.</b></p>{{element:0}}`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_FLASH), mode: 'saisie', strict: true, elements_additionnels: elImage(m) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { N });
    return payload;
};

// ─── Carte 2, format A : QCM « quel calcul ? » ────────────────────────────

const tousCandidats = (m, N) => {
    const cle = m.valeur(N);
    const c = [];
    const pousser = (code, texte, val, memeForme) => {
        if (val !== cle && !c.some((x) => x.val === val)) c.push({ code, texte, val, memeForme });
    };
    pousser('ajout_de_trop', calcul(m.fixe + m.groupe, N, m.groupe), m.fixe + m.groupe + N * m.groupe, true);
    if (m.fixe !== m.groupe) pousser('fixe_ajout_confondus', calcul(m.groupe, N, m.fixe), m.groupe + N * m.fixe, true);
    pousser('ajout_manquant', calcul(m.fixe, N - 1, m.groupe), m.fixe + (N - 1) * m.groupe, false);
    pousser('proportionnel', `${N} × ${m.groupe}`, N * m.groupe, false);
    pousser('addition', `${m.fixe} + ${N} + ${m.groupe}`, m.fixe + N + m.groupe, false);
    return c;
};

/** Deux distracteurs : un de la forme de la clé (a + N × b), l'autre libre parmi les restants. */
const distracteursQcm = (m, N) => {
    const cands = tousCandidats(m, N);
    const memeForme = cands.filter((x) => x.memeForme);
    const premier = memeForme.length ? randomChoice(memeForme) : null;
    if (!premier) return null;
    const restants = cands.filter((x) => x !== premier);
    return restants.length ? shuffle([premier, randomChoice(restants)]) : null;
};

const genQcm = () => {
    for (let essai = 0; essai < 50; essai++) {
        const m = tirer();
        const N = etape();
        const faux = distracteursQcm(m, N);
        if (!faux) continue;
        const bon = calcul(m.fixe, N, m.groupe);
        const texte = `{{element:0}}<p><b>Quel calcul donne le nombre ${deNom(m.nom)} à l'étape ${N} ?</b></p>`;
        const payload = makeQcm([bon, ...faux.map((x) => x.texte)], bon, texte, { ...opts(L_FIXE), elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { N, distracteurs: faux.map(({ code }) => ({ code })) });
        return payload;
    }
    // Repli déterministe : croix de points, étape 10 → 1 + 10 × 4 ; 5 + 10 × 4 (ajout_de_trop) ; 10 × 4 (proportionnel).
    const m = tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true });
    const payload = makeQcm(['1 + 10 × 4', '5 + 10 × 4', '10 × 4'], '1 + 10 × 4', `{{element:0}}<p><b>Quel calcul donne le nombre de points à l'étape 10 ?</b></p>`, { ...opts(L_FIXE), elements_additionnels: elImage(m) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { N: 10, distracteurs: [{ code: 'ajout_de_trop' }, { code: 'proportionnel' }] });
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux ─────────────────────────────────────

/** « À l'étape 10, il y a 1 + 10 × 4 points. » ; fausse avec une famille, différente de la clé en valeur. */
const ligneCalcul = (m, N, vraie) => {
    if (vraie) return { ligne: `À l'étape ${N}, il y a ${calcul(m.fixe, N, m.groupe)} ${m.nom}.`, vraie: true, code: null };
    const d = randomChoice(tousCandidats(m, N));
    return { ligne: `À l'étape ${N}, il y a ${d.texte} ${m.nom}.`, vraie: false, code: d.code };
};

/**
 * « À l'étape 10, il y a 10 groupes de 4 points. » Remplace « il y a 1 point
 * qui ne change pas d'une étape à l'autre » (retirée par le prof le
 * 2026-09-10) : au sens littéral, ce qui ne change pas d'une étape à l'autre,
 * c'est TOUT le motif précédent, donc la réponse dépendait de la lecture.
 */
const ligneGroupes = (m, N, vraie) => {
    if (vraie) return { ligne: `À l'étape ${N}, il y a ${N} groupes de ${pluriel(m.groupe, m.nom)}.`, vraie: true, code: null };
    const faux = [
        { code: 'ajout_manquant', g: N - 1, t: m.groupe },
        { code: 'pas_mal_lu', g: N, t: m.groupe + 1 },
        { code: 'pas_mal_lu', g: N, t: m.groupe - 1 },
    ].filter((d) => d.g > 1 && d.t > 1 && !(d.g === N && d.t === m.groupe));
    const d = randomChoice(faux);
    return { ligne: `À l'étape ${N}, il y a ${d.g} groupes de ${pluriel(d.t, m.nom)}.`, vraie: false, code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const m = tirer();
        const etapes = [];
        for (let i = 0; etapes.length < 3 && i < 60; i++) {
            const N = etape();
            if (!etapes.includes(N)) etapes.push(N);
        }
        if (etapes.length < 3) continue;
        const sortes = shuffle(['calcul', 'groupes', randomChoice(['calcul', 'groupes'])]);
        // La sorte tirée deux fois donne une ligne VRAIE et une FAUSSE : sinon
        // le tableau portait deux phrases jumelles à l'étape près.
        const doublon = sortes.find((s, i) => sortes.indexOf(s) !== i);
        const veritesDoublon = shuffle([true, false]);
        let vus = 0;
        const affs = sortes.map((s, i) => {
            const vraie = s === doublon ? veritesDoublon[vus++] : randomChoice([true, false]);
            return s === 'calcul' ? ligneCalcul(m, etapes[i], vraie) : ligneGroupes(m, etapes[i], vraie);
        });
        const nbVraies = affs.filter((a) => a.vraie).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const texte = `<p><b>Vrai ou faux ?</b></p>{{element:0}}`;
        const payload = makeVraiFaux(lignes, affs.map((a) => (a.vraie ? 0 : 1)), texte, { ...opts(L_FIXE), elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { verites: affs.map((a) => a.vraie), distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) });
        return payload;
    }
    // Repli déterministe : « étape 10 : 5 + 10 × 4 » faux ; « étape 7 : 1 + 7 × 4 » vrai ; « étape 12 : 12 groupes de 4 » vrai.
    const m = tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true });
    const payload = makeVraiFaux(
        [`À l'étape 10, il y a ${calcul(m.fixe + m.groupe, 10, m.groupe)} ${m.nom}.`,
            `À l'étape 7, il y a ${calcul(m.fixe, 7, m.groupe)} ${m.nom}.`,
            `À l'étape 12, il y a 12 groupes de ${pluriel(m.groupe, m.nom)}.`],
        [1, 0, 0], `<p><b>Vrai ou faux ?</b></p>{{element:0}}`, { ...opts(L_FIXE), elements_additionnels: elImage(m) },
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { verites: [false, true, true], distracteurs: [{ code: 'ajout_de_trop' }] });
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'figures STRUCTURÉES de la banque commune (./motifs.js) : croix 1 + 4n, L 1 + 2n, cadre 4 + 4n, tours 3 + 2n, allumettes en carrés 1 + 3n et en triangles 1 + 2n ; rien d\'écrit sur la figure, la longueur d\'une branche est le numéro de l\'étape ; une figure sur DEUX a une part fixe autre que 1 (sinon « 1 + N × groupe » se répondait sans regarder) ; rangées et figures croissantes exclues (aucune structure affine à lire)',
        'le couple (fixe 1, groupe 3), la frise de carrés en allumettes, SACRIFIÉ pour la fiche',
        'le calcul est toujours « fixe + N × groupe », jamais sa valeur (1061) ; chaque distracteur est différent de la clé EN VALEUR (les deux lectures 1 + 10 × 4 et 5 + 9 × 4 valent 41)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    fixe_qcm_unique: '« Quel calcul donne le nombre de points à l\'étape 10 ? », trois choix : la clé, un distracteur de la forme de la clé (ajout_de_trop, la principale ; fixe_ajout_confondus) et un libre (ajout_manquant, proportionnel, addition) ; résidu « forme a + N × b » mesuré à la revue',
    fixe_tableau_vf: 'trois phrases sur trois étapes distinctes, 1 ou 2 vraies, au moins une de chaque sorte : « à l\'étape N, il y a fixe + N × groupe » (fausses : les cinq familles du QCM) et « à l\'étape N, il y a N groupes de k » (fausses ajout_manquant N − 1 groupes, pas_mal_lu groupes de k ± 1, soit le côté partagé compté deux fois sur les allumettes) ; la sorte tirée deux fois donne une vraie et une fausse ; retirées à la revue du 2026-09-10 : « il y a f points qui ne changent pas » (au sens littéral tout le motif précédent ne change pas) et la lecture de 1060 donnée VRAIE',
    flash_texte_trou_saisie: 'une figure, deux lignes, quatre trous, le groupe imprimé : « Étape 3 : ⬚ + ⬚ × 4 » puis « Étape N : ⬚ + ⬚ × 4 » ; une fois sur deux N vaut 25, 50 ou 100 : le rang lointain est gratuit ici (la valeur n\'est jamais calculée) et il montre que l\'itération est morte',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    fixe_qcm_unique: genQcm,
    fixe_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'fixe', label: L_FIXE, formatsList: [
        { key: 'fixe_qcm_unique', label: 'QCM' },
        { key: 'fixe_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // mémoire courte : jamais deux fois la même variante dans une série (cf. sansRepetition)
    const payload = sansRepetition(key, GENERATORS[key]);
    payload._meta.generatorKey = key;
    return payload;
};
